import React, { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Snackbar,
  Alert,
  Backdrop,
  Avatar,
} from "@mui/material";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../SharedLink";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import axiosInstance from "../../api/axiosInstance";

const UpdatePassword = ({ email }) => {
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [countdownSnackbarOpen, setCountdownSnackbarOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const sharedLink = useSharedLink();

  const validatePassword = (value) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/;
    if (!regex.test(value)) {
      setPasswordError(
        "Password must contain at least one lowercase, one uppercase, one number, one special character, and be at least 5 characters long."
      );
    } else {
      setPasswordError(""); // Reset error if validation passes
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordError) {
      toast.error("Please fix the password error before submitting.");
      return;
    }
    try {
      const res = await axiosInstance.post(
        `${sharedLink}/api/updatePassword`,
        {
          email,
          newPassword,
        }
      );
      if (res.status === 200) {
        toast.success(res.data);
        setSnackbarOpen(true);
        setBackdropOpen(true);
      } else {
        toast.error(res.data);
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating the password.");
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setCountdownSnackbarOpen(true);
    const interval = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown === 1) {
          clearInterval(interval);
          setBackdropOpen(false);
          navigate("/login");
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const handleCountdownSnackbarClose = () => {
    setCountdownSnackbarOpen(false);
    setBackdropOpen(false);
  };

  return (
    <Container
      sx={{
        height: "80vh",
        width: "87vw", // Adjusted for a more centered look
        backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(10px)", // Glass effect
        borderRadius: "10px", // Optional: adds rounded corners
        border: "1px solid rgba(255, 255, 255, 0.18)", // Optional: adds a subtle border
        position: "absolute", // Ensures it stays in the center
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)", // Adjusts the positioning to truly center the div
        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)", // Optional: adds a subtle shadow for depth
        overflow: "hidden", // Ensures no overflow outside the container
      }}
    >
      <Box>
        <Typography
          className="login-message"
          variant="h3"
          gutterBottom
          sx={{ ml: 10 }}
        >
          Update
        </Typography>

        <Typography
          className="login-message"
          variant="h3"
          gutterBottom
          sx={{
            ml: 10,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          }}
        >
          Your Password.
        </Typography>
      </Box>
      <Box
        className="login-container"
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 4,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          marginLeft: "auto",
          marginRight: "20px",
          width: "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <ErrorRoundedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Update Password
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          type="email"
          label="Email"
          value={email}
          disabled
        />
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="New Password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            validatePassword(e.target.value);
          }}
          required
          error={!!passwordError}
          helperText={passwordError}
        />
        <Button
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          sx={{
            mt: 3,
            mb: 2,
            backgroundColor: "#e9a132",
            "&:hover": { backgroundColor: "#d8902e" },
          }}
        >
          Update Password
        </Button>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          Password updated successfully! Redirecting to login page...
        </Alert>
      </Snackbar>

      <Snackbar
        open={countdownSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCountdownSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCountdownSnackbarClose} severity="info">
          Redirecting you to the login page in {countdown}...
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={backdropOpen}
      />
    </Container>
  );
};

export default UpdatePassword;